import { useSyncExternalStore } from "react";

/**
 * Which theme the visitor asked for, and which one that resolves to.
 *
 * ==========================================================================================
 * IT IS A SUBPATH — `@gladis/ui/theme` — FOR THE SAME REASON `motion.ts` IS
 * ==========================================================================================
 *
 * `app-background.tsx` needs the resolved theme to pick the shader's three colours, and it renders
 * behind every page including sign-in, before any route has resolved. Reached through the package
 * index it would drag the entire component barrel onto the first-paint path of every route — the
 * regression `lib/motion.ts` documents at length, whose only symptom was a one-in-four flake in an
 * e2e spec three features away. `first-paint-imports.test.ts` fires on the import, and it covers
 * this module by covering that file.
 *
 * ==========================================================================================
 * THE CHOICE IS A DEVICE PREFERENCE, SO IT LIVES IN `localStorage` AND NOT IN POSTGRES
 * ==========================================================================================
 *
 * A column on `public.users` would sync the choice across devices, and would also mean the theme
 * could not be known until `/me` had resolved — so the app would paint dark, then repaint light, on
 * every load. The fix for that is a `localStorage` cache in front of the column, which is this
 * module plus a migration. The preference is genuinely per-device anyway: a laptop in a bright
 * office and a phone in bed are not obliged to agree.
 *
 * ==========================================================================================
 * WHAT APPLIES THE THEME, AND WHY IT IS NOT AN EFFECT
 * ==========================================================================================
 *
 * `<html data-theme>` is stamped by the inline script in `apps/app/index.html` BEFORE the bundle is
 * fetched — that is the whole point of it being inline, and without it a light-mode visitor gets a
 * black flash on every load while React boots.
 *
 * After that, every path that can change the answer re-applies it here: `setThemeChoice` when the
 * visitor picks one, and `onExternalChange` when the OS flips its own setting under a `system`
 * choice or another tab writes the key. Doing it in a `useEffect` instead would put DOM application
 * one paint behind, and would put it in whichever components happened to be mounted — the rail's
 * picker is not mounted on the sign-in screen, and `system` still has to work there.
 *
 * `color-scheme` is NOT set here. It is declared in `tokens.css` beside the tokens it belongs with,
 * so there is one owner of what a theme means rather than two that can disagree.
 */

export type ThemeChoice = "light" | "dark" | "system";

/** What `system` collapses to once the OS has been asked. The only two values `data-theme` takes. */
export type ResolvedTheme = "light" | "dark";

/**
 * The `localStorage` key, exported because `apps/app/index.html` has to spell it out literally — an
 * inline pre-paint script cannot import a module. `theme-storage-key.test.ts` asserts the two agree;
 * a silent disagreement would look like a picker that forgets, one paint at a time.
 */
export const THEME_STORAGE_KEY = "gladis-theme";

/** The attribute `tokens.css` selects on. Exported for the same reason as the key above. */
export const THEME_ATTRIBUTE = "data-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * `dark`, because that is what `:root` declares with no attribute at all.
 *
 * Every fallback in this module returns it: a browser with `localStorage` disabled, a render with no
 * `window`, a `catch`. The rule is that JavaScript failing should leave the visitor with the theme
 * the stylesheet already gives them, never with a document whose attribute and stylesheet disagree.
 */
const DEFAULT_RESOLVED: ResolvedTheme = "dark";

const listeners = new Set<() => void>();

/**
 * The choice as this page knows it, which is not always what storage knows.
 *
 * ==========================================================================================
 * MEMORY IS THE SOURCE OF TRUTH; STORAGE IS A WRITE-THROUGH CACHE
 * ==========================================================================================
 *
 * "A blocked-storage browser gets a picker that works and forgets" only holds if the choice survives
 * the rest of the page — and until feature 021 it did not. `setThemeChoice` wrote, then RE-READ
 * storage to decide what to apply. In a Safari private window both halves throw, so the write failed,
 * the read returned nothing, and the visitor's choice was discarded a microsecond after they made it.
 * The picker did NOTHING rather than forgetting, which is the opposite of the documented intent.
 *
 * `theme-store.test.tsx` claimed to cover this and asserted only `not.toThrow()` — satisfied by a
 * picker that does nothing. It now asserts the document.
 *
 * It also removes a `localStorage` hit from every render: `useSyncExternalStore` calls the snapshot
 * function during render, on every subscribed component.
 *
 * `null` means "not yet read", not "no choice" — `system` is a real value that must be
 * distinguishable from an unread memo.
 */
let choiceMemo: ThemeChoice | null = null;

/**
 * Reads the choice, treating anything unrecognised as `system`.
 *
 * `localStorage` THROWS rather than returning null in a Safari private window and wherever site data
 * is blocked outright. An unhandled throw here would be thrown during render — see the memo above —
 * so it would blank the entire application over a preference.
 */
export function readThemeChoice(): ThemeChoice {
  if (choiceMemo !== null) return choiceMemo;

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    choiceMemo = stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  } catch {
    choiceMemo = "system";
  }

  return choiceMemo;
}

/** Drops the memo so the next read goes back to storage. For another tab's write, and for tests. */
export function forgetThemeMemo(): void {
  choiceMemo = null;
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

/** The choice with `system` collapsed. Pure, so the picker can label the next state without asking. */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === "system" ? systemTheme() : choice;
}

/**
 * Puts the resolved theme on `<html>`, which is the only thing that actually changes the appearance.
 *
 * Returns what it applied so a caller need not resolve twice.
 */
export function applyResolvedTheme(): ResolvedTheme {
  const resolved = resolveTheme(readThemeChoice());
  document.documentElement.setAttribute(THEME_ATTRIBUTE, resolved);
  return resolved;
}

/**
 * Records a choice and applies it in the same tick.
 *
 * The write is allowed to fail — see `readThemeChoice` for where it throws — and when it does the
 * theme still changes for this page, it simply is not remembered. That is the right way round: a
 * blocked-storage browser gets a picker that works and forgets, not a picker that does nothing.
 */
export function setThemeChoice(choice: ThemeChoice): void {
  // The memo FIRST, so what follows applies the choice that was made rather than what storage
  // managed to keep. Write-through second, and it is allowed to fail.
  choiceMemo = choice;

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    /* Storage is unavailable. The DOM update below is what the visitor actually asked for. */
  }
  applyResolvedTheme();
  for (const listener of listeners) listener();
}

/**
 * Re-applies and re-broadcasts when something outside this tab changes the answer.
 *
 * TWO SOURCES, and both are easy to forget. The OS flipping to dark at sunset must move a `system`
 * visitor immediately — that is the entire meaning of the option. And a `storage` event fires when
 * ANOTHER tab writes the key, so two open tabs do not sit on opposite themes until one is reloaded.
 */
function onExternalChange(): void {
  // The memo is this page's answer, and a `storage` event means another page changed it. Dropping it
  // is what makes that half work at all.
  forgetThemeMemo();
  applyResolvedTheme();
  for (const listener of listeners) listener();
}

/**
 * The subscribed `MediaQueryList`, held because `matchMedia` MINTS A NEW OBJECT ON EVERY CALL.
 *
 * `detach` calling `window.matchMedia(DARK_QUERY).removeEventListener(...)` would remove the
 * listener from a list nothing was ever added to — leaving the real one attached to a list that is
 * now unreachable, so it can never be removed. The app mounts and unmounts this store on every
 * sign-out, and the leaked listeners accumulate silently. `motion.ts` avoids the same trap by
 * closing over the list inside `subscribe`; this module cannot, because its listener has to outlive
 * any single subscriber to keep `<html>` in step.
 */
let watched: MediaQueryList | null = null;

function attach(): void {
  watched = window.matchMedia(DARK_QUERY);
  watched.addEventListener("change", onExternalChange);
  window.addEventListener("storage", onExternalChange);
}

function detach(): void {
  watched?.removeEventListener("change", onExternalChange);
  watched = null;
  window.removeEventListener("storage", onExternalChange);
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  if (listeners.size === 1) attach();
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) detach();
  };
}

function choiceSnapshot(): ThemeChoice {
  return readThemeChoice();
}

function resolvedSnapshot(): ResolvedTheme {
  return resolveTheme(readThemeChoice());
}

function serverChoice(): ThemeChoice {
  return "system";
}

function serverResolved(): ResolvedTheme {
  return DEFAULT_RESOLVED;
}

export interface Theme {
  /** What the visitor picked, including `system`. What the picker labels itself with. */
  choice: ThemeChoice;
  /** What that means right now. What a consumer picking colours should branch on. */
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
}

/**
 * The theme, for any component that needs to know it.
 *
 * TWO `useSyncExternalStore` CALLS RETURNING STRINGS, not one returning this object. `getSnapshot`
 * must be referentially stable between changes or React re-renders forever looking for a fixed
 * point, and a fresh object literal never is. Strings compare by value, so both are stable for free
 * and the object below is an ordinary render value.
 */
export function useTheme(): Theme {
  const choice = useSyncExternalStore(subscribe, choiceSnapshot, serverChoice);
  const resolved = useSyncExternalStore(subscribe, resolvedSnapshot, serverResolved);

  return { choice, resolved, setChoice: setThemeChoice };
}
